'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/drawers/styles.css';
import { delegateBottomSheet } from 'BomdisoftComponents/drawers/delegate';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { type ReactNode, useEffect, useRef } from 'react';

export type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** `false` keeps the sheet up until a row is chosen. Only for a sheet whose
   * choice cannot be deferred; every other sheet must be dismissible, because a
   * sheet the user cannot back out of is a trap on a phone. */
  dismissible?: boolean;
  /** No inline padding: for content that carries its own, a dialog's title,
   * content and actions, whose hairlines must reach the sheet's edges. */
  flush?: boolean;
  /** The sheet's name. One of the two is required: a plain string, or the id
   * of a heading inside the sheet, which is how a form dialog names the sheet
   * it becomes by its own title. */
  label?: string;
  labelledBy?: string;
  testId?: string;
};

export const BottomSheet = ({ children, className, dismissible = true, flush, label, labelledBy, onClose, open, testId }: BottomSheetProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  useEffect(() => {
    const sheet = ref.current;
    if (!sheet) return;
    if (open && !sheet.open) sheet.showModal();
    if (!open && sheet.open) sheet.close();
    // A sheet dismissed by dragging keeps the offset it was released at; the
    // next open must start from the bottom rather than part-way up.
    if (!open) {
      sheet.style.translate = '';
      delete sheet.dataset.dragging;
    }
  }, [open]);

  useEffect(() => {
    const sheet = ref.current;
    if (!sheet) return;
    return delegateBottomSheet(sheet, { dismissible, onClose: () => onCloseRef.current() });
  }, [dismissible]);

  return (
    <dialog aria-label={label} aria-labelledby={labelledBy} className={joinClasses('bomdisoft-bottom-sheet', className)} data-flush={flush ? '' : undefined} data-testid={testId} ref={ref}>
      <span className='bomdisoft-bottom-sheet-handle' data-testid={testId ? `${testId}-handle` : undefined}>
        <span aria-hidden='true' className='bomdisoft-bottom-sheet-grip' />
      </span>
      {children}
    </dialog>
  );
};
